import { useEffect, useState } from "react";
import { Breadcrumb, Button, Tabs } from "flowbite-react";
import { HiHome, HiPlus, HiCheckCircle, HiRefresh } from "react-icons/hi";
import { useTranslation } from "react-i18next";
import NavbarSidebarLayout from "@/layouts/navbar-sidebar";
import LoadingPage from "@/components/LoadingPage";
import { toast } from "@/lib/toast";
import { getErrorMessage } from "@/utils/requestUtils";
import { KbIssue, KbSolution, Ticket, DataGridPagination, QueryParams } from "@/sharedTypes";
import { useEffectiveRoles, hasAnyRole } from "@/hooks/useEffectiveRoles";
import { agentRoles } from "@/constants/agent";

import {
  useGetKbIssuesQuery,
  useCreateKbIssueMutation,
  useUpdateKbIssueMutation,
  useDeleteKbIssueMutation,
  useGetKbIssueByIdQuery,
  useLinkSolutionToIssueMutation,
  useUnlinkSolutionFromIssueMutation,
  useBulkApprovePreCheckMutation,
  useBulkApproveMutation,
  useReVectorizeMutation,
  BulkApprovePreCheckResult,
  downloadKbIssuesReportPdf,
} from "@/api/kbApi/kbIssue";
import { useGetCompanyDetailsQuery } from "@/api/companyApi/company";
import {
  useGetKbSolutionsQuery,
  useCreateKbSolutionMutation,
  useUpdateKbSolutionMutation,
  useDeleteKbSolutionMutation,
  useGetKbSolutionByIdQuery,
} from "@/api/kbApi/kbSolution";

import KbIssueTable from "@/components/knowledgeBase/KbIssueTable";
import KbSolutionTable from "@/components/knowledgeBase/KbSolutionTable";
import KbIssueFormDialog from "@/components/knowledgeBase/KbIssueFormDialog";
import KbSolutionFormDialog from "@/components/knowledgeBase/KbSolutionFormDialog";
import KbIssueDetailModal from "@/components/knowledgeBase/KbIssueDetailModal";
import KbSolutionDetailModal from "@/components/knowledgeBase/KbSolutionDetailModal";
import KbDeleteConfirmModal from "@/components/knowledgeBase/KbDeleteConfirmModal";
import BulkApproveDialog from "@/components/knowledgeBase/BulkApproveDialog";
import TicketAiTable from "@/components/knowledgeBase/TicketAiTable";
import KbFilterBar from "@/components/knowledgeBase/KbFilterBar";
import TicketAiEditModal from "@/components/knowledgeBase/TicketAiEditModal";
import TicketAiApproveDialog from "@/components/knowledgeBase/TicketAiApproveDialog";
import {
  useGetResolvedTicketsForAiReviewQuery,
  useUpdateTicketAiFieldsMutation,
  useTicketAiApprovePreCheckMutation,
  useTicketAiBulkApproveMutation,
  useReVectorizeTicketsMutation,
  TicketAiPreCheckResult,
} from "@/api/ticketApi/ticketAi";

export default function KnowledgeBasePage() {
  const { t } = useTranslation(["knowledgeBase", "common"]);
  const { data: companyData } = useGetCompanyDetailsQuery();
  const approvalLanguage: "en" | "tr" =
    companyData?.company_language?.toLowerCase().startsWith("tr") ? "tr" : "en";

  // AI approval is for the admin tier: company admins, Managers (management
  // job role — promoted to company_admin by useEffectiveRoles) and super
  // admins. The backend enforces this too; this just hides the button.
  const effectiveRoles = useEffectiveRoles();
  const canApproveAi = hasAnyRole(effectiveRoles, [
    agentRoles.company_admin,
    agentRoles.super_admin,
  ]);

  // Issues state
  const [issueQueryParams, setIssueQueryParams] = useState<QueryParams>({
    filters: {},
    pagination: { page: 1, pageSize: 15 },
  });
  const [issueSearch, setIssueSearch] = useState("");
  const [isIssueFormOpen, setIsIssueFormOpen] = useState(false);
  const [issueFormMode, setIssueFormMode] = useState<"create" | "edit">("create");
  const [editingIssue, setEditingIssue] = useState<KbIssue | null>(null);
  const [viewingIssueId, setViewingIssueId] = useState<number | null>(null);
  const [deletingIssue, setDeletingIssue] = useState<KbIssue | null>(null);

  // Bulk approve state
  const [selectedIssueIds, setSelectedIssueIds] = useState<number[]>([]);
  const [isApproveDialogOpen, setIsApproveDialogOpen] = useState(false);
  const [preCheckResult, setPreCheckResult] = useState<BulkApprovePreCheckResult | null>(null);

  // PDF report download
  const [isDownloadingReport, setIsDownloadingReport] = useState(false);
  const [reportStartDate, setReportStartDate] = useState("");
  const [reportEndDate, setReportEndDate] = useState("");

  // Solutions state
  const [solutionQueryParams, setSolutionQueryParams] = useState<QueryParams>({
    filters: {},
    pagination: { page: 1, pageSize: 15 },
  });
  const [solutionSearch, setSolutionSearch] = useState("");
  const [isSolutionFormOpen, setIsSolutionFormOpen] = useState(false);
  const [solutionFormMode, setSolutionFormMode] = useState<"create" | "edit">("create");
  const [editingSolution, setEditingSolution] = useState<KbSolution | null>(null);
  const [deletingSolution, setDeletingSolution] = useState<KbSolution | null>(null);
  const [viewingSolutionId, setViewingSolutionId] = useState<number | null>(null);

  // Ticket AI state
  const [ticketQueryParams, setTicketQueryParams] = useState<QueryParams>({
    filters: {},
    pagination: { page: 1, pageSize: 15 },
  });
  const [editingTicket, setEditingTicket] = useState<Ticket | null>(null);
  const [selectedTicketIds, setSelectedTicketIds] = useState<number[]>([]);
  const [isTicketApproveDialogOpen, setIsTicketApproveDialogOpen] = useState(false);
  const [ticketPreCheckResult, setTicketPreCheckResult] = useState<TicketAiPreCheckResult | null>(null);

  // API queries
  const [issuePollingInterval, setIssuePollingInterval] = useState(0);
  const [solutionPollingInterval, setSolutionPollingInterval] = useState(0);

  const { data: issuesData, isLoading: issuesLoading } = useGetKbIssuesQuery(issueQueryParams, {
    refetchOnMountOrArgChange: true,
    pollingInterval: issuePollingInterval,
  });
  const { data: solutionsData, isLoading: solutionsLoading } = useGetKbSolutionsQuery(
    solutionQueryParams,
    { refetchOnMountOrArgChange: true, pollingInterval: solutionPollingInterval }
  );
  // Use `currentData` instead of `data` so the modal doesn't flash the
  // previous issue/solution while the new one is loading (RTK Query's `data`
  // is sticky across arg changes).
  const { currentData: viewingIssueData } = useGetKbIssueByIdQuery(viewingIssueId!, {
    skip: !viewingIssueId,
    refetchOnMountOrArgChange: true,
  });
  const { currentData: viewingSolutionData } = useGetKbSolutionByIdQuery(viewingSolutionId!, {
    skip: !viewingSolutionId,
    refetchOnMountOrArgChange: true,
  });
  const [ticketPollingInterval, setTicketPollingInterval] = useState(0);
  const { data: ticketsData, isLoading: ticketsLoading } = useGetResolvedTicketsForAiReviewQuery(
    ticketQueryParams,
    { refetchOnMountOrArgChange: true, pollingInterval: ticketPollingInterval }
  );

  // Mutations
  const [createIssue] = useCreateKbIssueMutation();
  const [updateIssue] = useUpdateKbIssueMutation();
  const [deleteIssue] = useDeleteKbIssueMutation();
  const [createSolution] = useCreateKbSolutionMutation();
  const [updateSolution] = useUpdateKbSolutionMutation();
  const [deleteSolution] = useDeleteKbSolutionMutation();
  const [linkSolution] = useLinkSolutionToIssueMutation();
  const [unlinkSolution] = useUnlinkSolutionFromIssueMutation();
  const [bulkApprovePreCheck] = useBulkApprovePreCheckMutation();
  const [bulkApprove, { isLoading: isApproving }] = useBulkApproveMutation();
  const [reVectorize, { isLoading: isReVectorizing }] = useReVectorizeMutation();
  const [updateTicketAi] = useUpdateTicketAiFieldsMutation();
  const [ticketAiPreCheck] = useTicketAiApprovePreCheckMutation();
  const [ticketAiBulkApprove, { isLoading: isTicketApproving }] = useTicketAiBulkApproveMutation();
  const [reVectorizeTickets, { isLoading: isReVectorizingTickets }] = useReVectorizeTicketsMutation();

  // Start polling while any issue/solution is pending or processing, stop once all reach a terminal state.
  useEffect(() => {
    const hasActive = (issuesData?.items ?? []).some(
      (issue: any) => issue.vectorize_status === "pending" || issue.vectorize_status === "processing"
    );
    setIssuePollingInterval(hasActive ? 5000 : 0);
  }, [issuesData]);

  useEffect(() => {
    const hasActive = (solutionsData?.items ?? []).some(
      (s: any) => s.vectorize_status === "pending" || s.vectorize_status === "processing"
    );
    setSolutionPollingInterval(hasActive ? 5000 : 0);
  }, [solutionsData]);

  useEffect(() => {
    const hasActive = (ticketsData?.items ?? []).some(
      (ticket: any) => ticket.vectorize_status === "pending" || ticket.vectorize_status === "processing"
    );
    setTicketPollingInterval(hasActive ? 5000 : 0);
  }, [ticketsData]);

  // Debounce search inputs and fold them into query params.
  // Reset to page 1 so results land on the first page.
  useEffect(() => {
    const handle = setTimeout(() => {
      setIssueQueryParams((prev) => {
        const { search: _omit, ...rest } = prev.filters || {};
        const nextFilters = issueSearch.trim()
          ? { ...rest, search: issueSearch.trim() }
          : rest;
        return {
          ...prev,
          filters: nextFilters,
          pagination: { ...prev.pagination, page: 1 },
        };
      });
    }, 300);
    return () => clearTimeout(handle);
  }, [issueSearch]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setSolutionQueryParams((prev) => {
        const { search: _omit, ...rest } = prev.filters || {};
        const nextFilters = solutionSearch.trim()
          ? { ...rest, search: solutionSearch.trim() }
          : rest;
        return {
          ...prev,
          filters: nextFilters,
          pagination: { ...prev.pagination, page: 1 },
        };
      });
    }, 300);
    return () => clearTimeout(handle);
  }, [solutionSearch]);

  // Update a single status filter field on a query-params setter.
  // Empty value clears the filter; any change resets to page 1.
  const updateStatusFilter = (
    setQuery: React.Dispatch<React.SetStateAction<QueryParams>>,
    key: "vectorize_status" | "is_ai_approved" | "category_id" | "subcategory_id",
    value: string,
  ) => {
    setQuery((prev) => {
      const nextFilters = { ...(prev.filters || {}) };
      if (value === "") {
        delete nextFilters[key];
      } else {
        nextFilters[key] = value;
      }
      return {
        ...prev,
        filters: nextFilters,
        pagination: { ...prev.pagination, page: 1 },
      };
    });
  };

  // Update sort (value is "field:ORDER"); resets to page 1.
  const updateSort = (
    setQuery: React.Dispatch<React.SetStateAction<QueryParams>>,
    value: string,
  ) => {
    const [sortBy, order] = value.split(":");
    setQuery((prev) => ({
      ...prev,
      filters: { ...(prev.filters || {}), sortBy, order },
      pagination: { ...prev.pagination, page: 1 },
    }));
  };

  // Current sort value ("field:ORDER") for a query-params object.
  const sortValueOf = (qp: QueryParams) =>
    `${qp.filters?.sortBy ?? "createdAt"}:${qp.filters?.order ?? "DESC"}`;

  // Data extraction
  const issueItems = issuesData?.items || [];
  const solutionItems = solutionsData?.items || [];

  // Re-vectorize is only meaningful for issues that already went through vectorization
  // but ended up in a bad state (failed or stuck in processing).
  const hasReVectorizableSelected = issueItems.some(
    (issue: any) =>
      selectedIssueIds.includes(issue.id) &&
      (issue.vectorize_status === "failed" || issue.vectorize_status === "processing")
  );

  // Issue pagination
  const issuePagination: DataGridPagination = {
    pageIndex: issuesData?.pagination?.pageIndex || 1,
    pageSize: issuesData?.pagination?.pageSize || 15,
    totalPages: issuesData?.pagination?.totalPages || 1,
    totalItems: issuesData?.pagination?.totalItems || 0,
  };

  // Solution pagination
  const solutionPagination: DataGridPagination = {
    pageIndex: solutionsData?.pagination?.pageIndex || 1,
    pageSize: solutionsData?.pagination?.pageSize || 15,
    totalPages: solutionsData?.pagination?.totalPages || 1,
    totalItems: solutionsData?.pagination?.totalItems || 0,
  };

  // Issue handlers
  const handleCreateIssue = (data: Partial<KbIssue>) => {
    createIssue(data)
      .unwrap()
      .then(() => {
        toast.success(t("knowledgeBase:issueCreated"));
        setIsIssueFormOpen(false);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handleUpdateIssue = (data: Partial<KbIssue>) => {
    if (!editingIssue?.id) return;
    updateIssue({ id: editingIssue.id, ...data })
      .unwrap()
      .then(() => {
        toast.success(t("knowledgeBase:issueUpdated"));
        setIsIssueFormOpen(false);
        setEditingIssue(null);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handleDeleteIssue = () => {
    if (!deletingIssue?.id) return;
    deleteIssue(deletingIssue.id)
      .unwrap()
      .then(() => {
        toast.success(t("knowledgeBase:issueDeleted"));
        setDeletingIssue(null);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  // Solution handlers
  const handleCreateSolution = (data: Partial<KbSolution>) => {
    createSolution(data)
      .unwrap()
      .then(() => {
        toast.success(t("knowledgeBase:solutionCreated"));
        setIsSolutionFormOpen(false);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handleUpdateSolution = (data: Partial<KbSolution>) => {
    if (!editingSolution?.id) return;
    updateSolution({ id: editingSolution.id, ...data })
      .unwrap()
      .then(() => {
        toast.success(t("knowledgeBase:solutionUpdated"));
        setIsSolutionFormOpen(false);
        setEditingSolution(null);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handleDeleteSolution = () => {
    if (!deletingSolution?.id) return;
    deleteSolution(deletingSolution.id)
      .unwrap()
      .then(() => {
        toast.success(t("knowledgeBase:solutionDeleted"));
        setDeletingSolution(null);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  // Link/Unlink handlers
  const handleLinkSolution = (issueId: number, solutionId: number) => {
    linkSolution({ issueId, kb_solution_id: solutionId, language: approvalLanguage })
      .unwrap()
      .then(() => toast.success(t("knowledgeBase:solutionLinked")))
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handleUnlinkSolution = (issueId: number, solutionId: number) => {
    unlinkSolution({ issueId, solutionId, language: approvalLanguage })
      .unwrap()
      .then(() => toast.success(t("knowledgeBase:solutionUnlinked")))
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  // Bulk approve handlers
  const handleBulkApproveClick = async () => {
    if (selectedIssueIds.length === 0) {
      toast.warning(t("knowledgeBase:selectIssuesToApprove"));
      return;
    }
    try {
      const result = await bulkApprovePreCheck({ issue_ids: selectedIssueIds }).unwrap();
      setPreCheckResult(result);
      setIsApproveDialogOpen(true);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleDownloadReport = async () => {
    setIsDownloadingReport(true);
    try {
      // Sorting doesn't apply to the report (it has its own order), everything
      // else the user filtered by should carry over.
      const { sortBy: _sortBy, order: _order, ...reportFilters } =
        issueQueryParams.filters ?? {};
      const blob = await downloadKbIssuesReportPdf({
        startDate: reportStartDate || undefined,
        endDate: reportEndDate || undefined,
        filters: reportFilters,
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `kb-report-${new Date().toISOString().split("T")[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(t("knowledgeBase:reportDownloadSuccess"));
    } catch (err: any) {
      // The download helper throws a plain Error, so prefer its message.
      toast.error(err?.message || getErrorMessage(err) || t("knowledgeBase:downloadReport"));
    } finally {
      setIsDownloadingReport(false);
    }
  };

  const handleReVectorize = async () => {
    if (selectedIssueIds.length === 0) {
      toast.warning(t("knowledgeBase:selectIssuesToReVectorize"));
      return;
    }
    try {
      await reVectorize({ issue_ids: selectedIssueIds, language: approvalLanguage }).unwrap();
      toast.success(t("knowledgeBase:reVectorizeQueued", { count: selectedIssueIds.length }));
      setSelectedIssueIds([]);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleReVectorizeTickets = async () => {
    if (selectedTicketIds.length === 0) {
      toast.warning(t("knowledgeBase:selectTicketsToReVectorize"));
      return;
    }
    try {
      await reVectorizeTickets({ ticket_ids: selectedTicketIds, language: approvalLanguage }).unwrap();
      toast.success(t("knowledgeBase:reVectorizeTicketsQueued", { count: selectedTicketIds.length }));
      setSelectedTicketIds([]);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleBulkApproveConfirm = async () => {
    try {
      const result = await bulkApprove({
        issue_ids: selectedIssueIds,
        approve_solutions: true,
        language: approvalLanguage,
      }).unwrap();
      toast.success(
        t("knowledgeBase:bulkApproveSuccess", {
          issues: result.approvedIssueCount,
          solutions: result.approvedSolutionCount,
        })
      );
      setIsApproveDialogOpen(false);
      setSelectedIssueIds([]);
      setPreCheckResult(null);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  // Ticket AI data
  const ticketItems = ticketsData?.items || [];
  const hasReVectorizableTicketsSelected = ticketItems.some(
    (ticket: any) =>
      selectedTicketIds.includes(ticket.ticket_id) &&
      (ticket.vectorize_status === "failed" || ticket.vectorize_status === "processing")
  );
  const ticketPagination: DataGridPagination = {
    pageIndex: ticketsData?.pagination?.pageIndex || 1,
    pageSize: ticketsData?.pagination?.pageSize || 15,
    totalPages: ticketsData?.pagination?.totalPages || 1,
    totalItems: ticketsData?.pagination?.totalItems || 0,
  };

  // Ticket AI handlers
  const handleTicketAiEdit = (data: Record<string, any>) => {
    if (!editingTicket?.ticket_id) return;
    updateTicketAi({ ticket_id: editingTicket.ticket_id, ...data })
      .unwrap()
      .then(() => {
        toast.success(t("knowledgeBase:ticketAiUpdated"));
        setEditingTicket(null);
      })
      .catch((err) => toast.error(getErrorMessage(err)));
  };

  const handleTicketApproveClick = async () => {
    if (selectedTicketIds.length === 0) {
      toast.warning(t("knowledgeBase:selectTicketsToApprove"));
      return;
    }
    try {
      const result = await ticketAiPreCheck({ ticket_ids: selectedTicketIds }).unwrap();
      setTicketPreCheckResult(result);
      setIsTicketApproveDialogOpen(true);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const handleTicketApproveConfirm = async () => {
    try {
      const result = await ticketAiBulkApprove({
        ticket_ids: selectedTicketIds,
        language: approvalLanguage,
      }).unwrap();

      toast.success(t("knowledgeBase:ticketApproveSuccess", { count: result.approvedCount }));
      setIsTicketApproveDialogOpen(false);
      setSelectedTicketIds([]);
      setTicketPreCheckResult(null);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const isLoading = issuesLoading || solutionsLoading;

  return (
    <NavbarSidebarLayout>
      {isLoading ? (
        <LoadingPage />
      ) : (
        <div className="h-screen overflow-y-auto pb-20">
          <div className="mb-6 grid grid-cols-1 gap-y-6 px-4 pt-6 dark:border-gray-700 dark:bg-gray-900">
            <div className="col-span-full space-y-6">
              <Breadcrumb className="mb-4">
                <Breadcrumb.Item href="/">
                  <div className="flex items-center gap-x-3">
                    <HiHome className="text-xl" />
                    <span className="dark:text-white">{t("common:home")}</span>
                  </div>
                </Breadcrumb.Item>
                <Breadcrumb.Item>{t("knowledgeBase:title")}</Breadcrumb.Item>
              </Breadcrumb>

              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white sm:text-2xl">
                  {t("knowledgeBase:title")}
                </h1>
                <p className="text-gray-500">{t("knowledgeBase:description")}</p>
              </div>

              <Tabs>
                {/* Issues Tab */}
                <Tabs.Item active title={t("knowledgeBase:issues")}>
                  <KbFilterBar
                    search={issueSearch}
                    onSearchChange={setIssueSearch}
                    searchPlaceholder={t("knowledgeBase:searchIssues")}
                    vectorizeStatus={String(issueQueryParams.filters?.vectorize_status ?? "")}
                    aiApproved={String(issueQueryParams.filters?.is_ai_approved ?? "")}
                    onVectorizeStatusChange={(v) =>
                      updateStatusFilter(setIssueQueryParams, "vectorize_status", v)
                    }
                    onAiApprovedChange={(v) =>
                      updateStatusFilter(setIssueQueryParams, "is_ai_approved", v)
                    }
                    showCategory
                    categoryId={String(issueQueryParams.filters?.category_id ?? "")}
                    subcategoryId={String(issueQueryParams.filters?.subcategory_id ?? "")}
                    onCategoryChange={(v) =>
                      updateStatusFilter(setIssueQueryParams, "category_id", v)
                    }
                    onSubcategoryChange={(v) =>
                      updateStatusFilter(setIssueQueryParams, "subcategory_id", v)
                    }
                    sort={sortValueOf(issueQueryParams)}
                    onSortChange={(v) => updateSort(setIssueQueryParams, v)}
                    enableReport
                    startDate={reportStartDate}
                    endDate={reportEndDate}
                    onStartDateChange={setReportStartDate}
                    onEndDateChange={setReportEndDate}
                    onDownload={handleDownloadReport}
                    isDownloading={isDownloadingReport}
                    actions={
                      <>
                        {canApproveAi && (
                          <Button color="success" onClick={handleBulkApproveClick}>
                            <HiCheckCircle className="mr-2 h-5 w-5" />
                            {t("knowledgeBase:bulkApprove")}
                            {selectedIssueIds.length > 0 && ` (${selectedIssueIds.length})`}
                          </Button>
                        )}
                        <Button
                          color="warning"
                          onClick={handleReVectorize}
                          disabled={isReVectorizing || !hasReVectorizableSelected}
                        >
                          <HiRefresh className="mr-2 h-5 w-5" />
                          {isReVectorizing
                            ? t("knowledgeBase:reVectorizingIssues")
                            : t("knowledgeBase:reVectorize")}
                          {selectedIssueIds.length > 0 && ` (${selectedIssueIds.length})`}
                        </Button>
                        <Button
                          color="primary"
                          onClick={() => {
                            setIssueFormMode("create");
                            setEditingIssue(null);
                            setIsIssueFormOpen(true);
                          }}
                        >
                          <HiPlus className="mr-2 h-5 w-5" />
                          {t("knowledgeBase:createIssue")}
                        </Button>
                      </>
                    }
                  />

                  <KbIssueTable
                    data={issueItems}
                    pagination={issuePagination}
                    onPageChange={(page) =>
                      setIssueQueryParams((prev) => ({
                        ...prev,
                        pagination: { ...prev.pagination, page },
                      }))
                    }
                    onEdit={(issue) => {
                      setIssueFormMode("edit");
                      setEditingIssue(issue);
                      setIsIssueFormOpen(true);
                    }}
                    onDelete={(issue) => setDeletingIssue(issue)}
                    onView={(issue) => setViewingIssueId(issue.id!)}
                    selectedIds={selectedIssueIds}
                    onSelectionChange={setSelectedIssueIds}
                  />
                </Tabs.Item>

                {/* Solutions Tab */}
                <Tabs.Item title={t("knowledgeBase:solutions")}>
                  {/* Action row */}
                  <KbFilterBar
                    search={solutionSearch}
                    onSearchChange={setSolutionSearch}
                    searchPlaceholder={t("knowledgeBase:searchSolutions")}
                    vectorizeStatus={String(solutionQueryParams.filters?.vectorize_status ?? "")}
                    aiApproved={String(solutionQueryParams.filters?.is_ai_approved ?? "")}
                    onVectorizeStatusChange={(v) =>
                      updateStatusFilter(setSolutionQueryParams, "vectorize_status", v)
                    }
                    onAiApprovedChange={(v) =>
                      updateStatusFilter(setSolutionQueryParams, "is_ai_approved", v)
                    }
                    sort={sortValueOf(solutionQueryParams)}
                    onSortChange={(v) => updateSort(setSolutionQueryParams, v)}
                    actions={
                      <Button
                        color="primary"
                        onClick={() => {
                          setSolutionFormMode("create");
                          setEditingSolution(null);
                          setIsSolutionFormOpen(true);
                        }}
                      >
                        <HiPlus className="mr-2 h-5 w-5" />
                        {t("knowledgeBase:createSolution")}
                      </Button>
                    }
                  />

                  <KbSolutionTable
                    data={solutionItems}
                    pagination={solutionPagination}
                    onPageChange={(page) =>
                      setSolutionQueryParams((prev) => ({
                        ...prev,
                        pagination: { ...prev.pagination, page },
                      }))
                    }
                    onEdit={(solution) => {
                      setSolutionFormMode("edit");
                      setEditingSolution(solution);
                      setIsSolutionFormOpen(true);
                    }}
                    onDelete={(solution) => setDeletingSolution(solution)}
                    onView={(solution) => setViewingSolutionId(solution.id!)}
                  />
                </Tabs.Item>
                {/* Resolved Tickets Tab */}
                <Tabs.Item title={t("knowledgeBase:resolvedTickets")}>
                  <KbFilterBar
                    search=""
                    onSearchChange={() => {}}
                    showSearch={false}
                    vectorizeStatus={String(ticketQueryParams.filters?.vectorize_status ?? "")}
                    aiApproved={String(ticketQueryParams.filters?.is_ai_approved ?? "")}
                    onVectorizeStatusChange={(v) =>
                      updateStatusFilter(setTicketQueryParams, "vectorize_status", v)
                    }
                    onAiApprovedChange={(v) =>
                      updateStatusFilter(setTicketQueryParams, "is_ai_approved", v)
                    }
                    actions={
                      <>
                        {canApproveAi && (
                          <Button color="success" onClick={handleTicketApproveClick}>
                            <HiCheckCircle className="mr-2 h-5 w-5" />
                            {t("knowledgeBase:bulkApprove")}
                            {selectedTicketIds.length > 0 && ` (${selectedTicketIds.length})`}
                          </Button>
                        )}
                        <Button
                          color="warning"
                          onClick={handleReVectorizeTickets}
                          disabled={isReVectorizingTickets || !hasReVectorizableTicketsSelected}
                        >
                          <HiRefresh className="mr-2 h-5 w-5" />
                          {isReVectorizingTickets
                            ? t("knowledgeBase:reVectorizingTickets")
                            : t("knowledgeBase:reVectorize")}
                          {selectedTicketIds.length > 0 && ` (${selectedTicketIds.length})`}
                        </Button>
                      </>
                    }
                  />

                  <TicketAiTable
                    data={ticketItems}
                    pagination={ticketPagination}
                    onPageChange={(page) =>
                      setTicketQueryParams((prev) => ({
                        ...prev,
                        pagination: { ...prev.pagination, page },
                      }))
                    }
                    onEdit={(ticket) => setEditingTicket(ticket)}
                    selectedIds={selectedTicketIds}
                    onSelectionChange={setSelectedTicketIds}
                  />
                </Tabs.Item>
              </Tabs>
            </div>
          </div>
        </div>
      )}

      {/* Issue Form Modal */}
      <KbIssueFormDialog
        open={isIssueFormOpen}
        onClose={() => {
          setIsIssueFormOpen(false);
          setEditingIssue(null);
        }}
        onSubmit={issueFormMode === "create" ? handleCreateIssue : handleUpdateIssue}
        mode={issueFormMode}
        initialData={editingIssue}
        vectorLanguage={approvalLanguage}
      />

      {/* Solution Form Modal */}
      <KbSolutionFormDialog
        open={isSolutionFormOpen}
        onClose={() => {
          setIsSolutionFormOpen(false);
          setEditingSolution(null);
        }}
        onSubmit={solutionFormMode === "create" ? handleCreateSolution : handleUpdateSolution}
        mode={solutionFormMode}
        initialData={editingSolution}
        vectorLanguage={approvalLanguage}
      />

      {/* Issue Detail Modal */}
      <KbIssueDetailModal
        open={!!viewingIssueId}
        onClose={() => setViewingIssueId(null)}
        issue={viewingIssueData || null}
        onLinkSolution={handleLinkSolution}
        onUnlinkSolution={handleUnlinkSolution}
      />

      {/* Solution Detail Modal */}
      <KbSolutionDetailModal
        open={!!viewingSolutionId}
        onClose={() => setViewingSolutionId(null)}
        solution={viewingSolutionData || null}
      />

      {/* Bulk Approve Dialog */}
      <BulkApproveDialog
        open={isApproveDialogOpen}
        onClose={() => {
          setIsApproveDialogOpen(false);
          setPreCheckResult(null);
        }}
        onConfirm={handleBulkApproveConfirm}
        preCheckResult={preCheckResult}
        isLoading={isApproving}
        selectedCount={selectedIssueIds.length}
      />

      {/* Delete Issue Confirm */}
      <KbDeleteConfirmModal
        open={!!deletingIssue}
        onClose={() => setDeletingIssue(null)}
        onConfirm={handleDeleteIssue}
        message={t("knowledgeBase:deleteIssueConfirm")}
      />

      {/* Delete Solution Confirm */}
      <KbDeleteConfirmModal
        open={!!deletingSolution}
        onClose={() => setDeletingSolution(null)}
        onConfirm={handleDeleteSolution}
        message={t("knowledgeBase:deleteSolutionConfirm")}
      />

      {/* Ticket AI Edit Modal */}
      <TicketAiEditModal
        open={!!editingTicket}
        onClose={() => setEditingTicket(null)}
        onSubmit={handleTicketAiEdit}
        ticket={editingTicket}
        kbIssues={issueItems}
        vectorLanguage={approvalLanguage}
      />

      {/* Ticket AI Approve Dialog */}
      <TicketAiApproveDialog
        open={isTicketApproveDialogOpen}
        onClose={() => {
          setIsTicketApproveDialogOpen(false);
          setTicketPreCheckResult(null);
        }}
        onConfirm={handleTicketApproveConfirm}
        preCheckResult={ticketPreCheckResult}
        isLoading={isTicketApproving}
        selectedCount={selectedTicketIds.length}
      />
    </NavbarSidebarLayout>
  );
}
